import type { CollectionHealthSummary } from '@siliconvault/shared';
import { Link } from '@tanstack/react-router';
import { useTranslation } from '../../i18n';

interface HealthDashboardProps {
  data: CollectionHealthSummary;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#FF3333',
  high: '#FF6600',
  medium: '#FFB800',
  low: '#33FF33',
};

export function HealthDashboard({ data }: HealthDashboardProps) {
  const { t } = useTranslation();
  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <StatCard label={t('healthDashboard.totalItems')} value={String(data.totalItems)} />
        <StatCard label={t('healthDashboard.working')} value={`${data.workingPercent}%`} color="#33FF33" />
        <StatCard label={t('healthDashboard.recapProgress')} value={`${data.recapProgress.percent}%`} color="#FFB800" />
        <StatCard label={t('healthDashboard.activeRetrobright')} value={String(data.retrobrightStats.active)} color="#7B68EE" />
        <StatCard label={t('healthDashboard.totalLinks')} value={String(data.linkageStats.totalLinks)} color="#00FFFF" />
      </div>

      {/* Condition breakdown */}
      <div style={{
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
      }}>
        <h3 style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>{t('healthDashboard.conditionBreakdown')}</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {Object.entries(data.conditionBreakdown).map(([condition, count]) => (
            <div key={condition} style={{
              padding: '0.375rem 0.75rem',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.75rem',
            }}>
              <span style={{ color: 'var(--text-muted)' }}>{condition}: </span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {data.urgentItems.length > 0 && (
        <div style={{
          padding: '1rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}>
          <h3 style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>{t('healthDashboard.urgentAlerts')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {data.urgentItems.map((alert, i) => (
              <Link
                key={i}
                to="/inventory/$itemId"
                params={{ itemId: alert.itemId }}
                search={{ from: undefined }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  color: 'inherit',
                  fontSize: '0.75rem',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: SEVERITY_COLORS[alert.severity] ?? '#888',
                  flexShrink: 0,
                }} />
                <span style={{ fontWeight: 600 }}>{alert.itemName}</span>
                <span style={{ color: 'var(--text-muted)', flex: 1 }}>{alert.title}</span>
                <span style={{
                  fontSize: '0.625rem',
                  color: SEVERITY_COLORS[alert.severity] ?? '#888',
                  textTransform: 'uppercase',
                }}>
                  {alert.severity}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '1rem',
    }}>
      <div style={{ fontSize: '0.625rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '1.5rem',
        fontWeight: 600,
        color: color ?? 'var(--accent)',
        lineHeight: 1,
      }}>
        {value}
      </div>
    </div>
  );
}
